import {Component, OnInit, ViewChild} from "@angular/core";
import {byNumeric} from 'iso-country-codes';
import {
  Card,
  CardStatusInfo,
  Chargeback,
  ChargebackCardState,
  ChargebackFileType,
  ChargebackReason,
  ChargebackStatus,
  ChargebackToFile,
  SecurityProcedure
} from "../../../dto/card";
import {ContentRowField} from "../../../shared/content-row/content-row.component";
import {
  formatPhone,
  phoneToCountry,
  toCardExpireDateString,
  toDateString,
  toDateTimeString,
  toDateTimeStringNoSeconds
} from "../../../providers/app-date-adapter";
import {GpsCardStatus, GpsCardStatusHelper} from "../../../dto/gps-card-status";
import {AddressChangeStatus, AddressStatus, IdentLevel} from "../../../dto/ident-status";
import {Customer} from "../../../dto/customer";
import {EventLog} from "../../../dto/event-log";
import {CardInfo} from "../../../dto/CardbroGateway/card-info";
import {LimitGroup} from "../../../dto/limitGroup";
import {Product} from "../../../dto/product";
import {ObjectComment} from "../../../dto/object-comment";
import {ActivatedRoute, Router} from "@angular/router";
import {Location} from "@angular/common";
import {MatDialog} from "@angular/material/dialog";
import {CardsService} from "../../services/cards.service";
import {RoleService} from "../../../services/role.service";
import {EventLogService} from "../../../services/crud/event-log.service";
import {ToastService} from "angular-toastify";
import {ProductService} from "../../services/product.service";
import {ObjectCommentsService} from "../../services/object-comment.service";
import {environment} from "../../../../environments/environment";
import {HttpErrorResponse} from "@angular/common/http";
import {
  StatusChangeReasonDialogComponent
} from "../../../shared/status-change-reason-dialog/status-change-reason-dialog.component";
import {ConfirmDialogComponent} from "../../../shared/confirm-dialog/confirm-dialog.component";
import {
  IncludeSecondariesDialogComponent
} from "../../../shared/include-secondaries-dialog/include-secondaries-dialog.component";
import {ChargebacksService} from "../../services/chargebacks.service";
import {Statement, StatementLineDto} from "../../../dto/CardbroGateway/statement";
import {
  AuthenticationMethod,
  CardDataInputMethod,
  CardholderPresent,
  CardPresent,
  GPSPosData,
  parseGPSPosData,
  SCATestResult
} from "../../../helpers/gps-pos-data-helper";
import {CardbroGatewayService} from "../../services/cardbro-gateway.service";
import {Transaction} from "../../../dto/CardbroGateway/transaction";
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FormGroup} from "@angular/forms";
import {CardHolderDetails} from "../../../dto/CardbroGateway/cardholder-details";
import {CustomersService} from "../../services/customers.service";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {
  getIdentStatusColor,
  getIdentStatusIcon,
  getIdentStatusText,
  getPepStatusColor, getPepStatusText
} from "../../../helpers/pep-status-helper";

@Component({
  selector: 'app-chargeback-detail',
  templateUrl: './chargeback-detail.component.html',
  styleUrls: ['./chargeback-detail.component.css']
})
export class ChargebackDetailComponent implements OnInit {

  isLoading = false;

  chargeback: Chargeback;
  filesList: ChargebackToFile[] = [];

  getCardStateString(cardState: ChargebackCardState) {
    if ( cardState == ChargebackCardState.HaveCard )
      return "Still have card";
    if ( cardState == ChargebackCardState.LostCard )
      return "Lost card";
    if ( cardState == ChargebackCardState.StolenCard )
      return "Card stolen";

    return "";
  }

  getReasonString(reason: ChargebackReason) {
    if ( reason == ChargebackReason.UnauthorizedAmount )
      return "Unauthorized Amount";
    if ( reason == ChargebackReason.UnauthorizedPayment )
      return "Unauthorized Payment";
    if ( reason == ChargebackReason.MultipleDebits )
      return "Multiple Debits";
    if ( reason == ChargebackReason.Other )
      return "Other";
    if ( reason == ChargebackReason.DebitAfterCancellation )
      return "Debit after cancellation";
    if ( reason == ChargebackReason.CounterfeitDefective )
      return "Counterfeit or defective";
    if ( reason == ChargebackReason.GoodsServiceNotProvided )
      return "Goods/Services not provided";
    if ( reason == ChargebackReason.NoCreditAfterReturn )
      return "No credit after return of goods";

    return "";
  }

  getSecurityProcedureString(procedure: SecurityProcedure) {
    if ( procedure == SecurityProcedure.CardPaymentWithPin )
      return "Card payment with PIN";
    if ( procedure == SecurityProcedure.ContactlessWithoutPin )
      return "Contactless without PIN";
    if ( procedure == SecurityProcedure.OnlinePaymentWithCvv3Ds )
      return "Online + 3DS";
    if ( procedure == SecurityProcedure.OnlinePaymentWithCvv )
      return "Online + CVV";
    if ( procedure == SecurityProcedure.NotKnown )
      return "Not known";


    return "";
  }

  fieldsCustomer = [
    {header: "Title", cell: (row: Customer) => `${row.title}`},
    {header: "First name", cell: (row: Customer) => `${row.firstName || ""}`} as ContentRowField<Customer>,
    {header: "Last name", cell: (row: Customer) => `${row.lastName || ""}`} as ContentRowField<Customer>,
    {header: "Address", cell: (row: Customer) => `${row.address || ""}`} as ContentRowField<Customer>,
    {header: "City", cell: (row: Customer) => `${row.city || ""}`} as ContentRowField<Customer>,
    {header: "Zip code", cell: (row: Customer) => `${row.zipCode || ""}`} as ContentRowField<Customer>,
    {header: "Email", cell: (row: Customer) => `${row.email || ""}`} as ContentRowField<Customer>,
    {header: "Phone", cell: (row: Customer) => `${row.phone || ""}`} as ContentRowField<Customer>,
    {header: "Date of birth", cell: (row: Customer) => `${toDateString(row.dob) || ""}`} as ContentRowField<Customer>,
    {
      header: "Country",
      cellFlag: (row: Customer) => byNumeric[row.countryIsoCode.toString().padStart(3, "0")].alpha2 || null,
      cell: (row: Customer) => `${row.countryName || ""}`
    } as ContentRowField<Customer>,


    {
      header: "Ident",
      iconColor: row => getIdentStatusColor(row.identStatus),
      icon: (row: Customer) => getIdentStatusIcon(row.identStatus),
      cell: row => getIdentStatusText(row.identStatus),
    } as ContentRowField<Customer>,
    {
      header: "PEP Status",
      iconColor: (row: Customer) => getPepStatusColor(row.pepStatus), icon: (row: Customer) => "perm_identity",
      cell: (row: Customer) => getPepStatusText(row.pepStatus),
    },
    {
      header: "Address Status",
      iconColor: (row: Customer) => {

        if (row.addressStatus == AddressStatus.Confirmed) {
          return "#8bc34a";
        }
        if (row.addressStatus == AddressStatus.WaitingForDocuments) {
          return "#4a82c3";
        }
        if (row.addressStatus == AddressStatus.PendingCheck) {
          return "#684ac3";
        }

        return "#d04848";
      },
      icon: (row: Customer) => {
        if (row.addressStatus == AddressStatus.Confirmed) {
          return "place";
        }
        if (row.addressStatus == AddressStatus.WaitingForDocuments) {
          return "not_listed_location";
        }
        if (row.addressStatus == AddressStatus.PendingCheck) {
          return "not_listed_location";
        }

        return "not_listed_location";
      }, cell: (row: Customer) => {
        if (row.addressStatus == AddressStatus.Confirmed) {
          return "Confirmed";
        }
        if (row.addressStatus == AddressStatus.WaitingForDocuments) {
          return "Waiting for documents";
        }
        if (row.addressStatus == AddressStatus.PendingCheck) {
          return "Pending check";
        }

        return "Unconfirmed";
      }
    },

  ] as ContentRowField<Customer>[];

  fields1 = [
    { header: 'Token', cell: (row: Card) => `${row.token}`, copyCellActive: (row: Card) => true, },
    {
      header: 'Primary Token',
      //icon: (row: Card) => row.primaryCardId ? "visibility" : null,
      link: (row: Card) => row.primaryCardId ? '../../cards/' + row.primaryCardId : null,
      cell: (row: Card) => row.primaryCardId ? `➡️ ${row.primaryToken}` : '(This is a primary card)',
      copyCellActive: (row: Card) => !!row.primaryCardId,
      cellColor: (row: Card) => row.primaryCardId ? '#2222cc' : null,
    },

  ] as ContentRowField<Card>[];

  fields2 = [
    { header: 'Card name', cell: (row: Card) => `${row.cardName || ''}` },
    { header: 'PAN'      , cell: (row: Card) => `${row.maskedPAN || '(unknown)' }` },
    { header: 'Product', cell: (row: Card) => `${row.product.internalName}` },
    { header: 'Company', cell: (row: Card) => `${row.companyName}` },
    { header: 'Exp date', cell: (row: Card) => `${toCardExpireDateString(row.expireDate) || ''}` },
    { header: 'Card Type', cell: (row: Card) => `${row.cardTypeName || '(unknown)'}`},
    { header: 'Status', cell: (row: Card) => `${row.statusName || ''}` },

    { header: 'Creation Date', cell: (row: Card) => `${toDateTimeStringNoSeconds(row.creationDate) || ''}` },
    { header: 'Activation date', cell: (row: Card) => `${toDateTimeStringNoSeconds(row.activationDate) || ''}` },

  ] as ContentRowField<Card>[];


  fieldsChargeback = [

    { header: 'Customer', icon: row => 'visibility', link: row => '../../customers-overview/' + (row.customerId || ''), cell: row => `${row.customer.firstName + ' ' + row.customer.lastName || ''}` } as ContentRowField<Chargeback>,
    { header: 'Card', icon: row => 'visibility', link: row => '../../cards/' + (row.cardId || ''), cell: row => `${row.card.token || ''}` } as ContentRowField<Chargeback>,

    { header: 'Request Date', cell: (row: Chargeback) => `${toDateTimeStringNoSeconds(row.creationDate) || ''}` },
    { header: 'Status', cell: (row: Chargeback) => this.getStatusString(row.status) },

    { header: 'Chargeback Reason', cell: (row: Chargeback) => this.getReasonString(row.reason) },
    { header: 'Security Procedure', cell: (row: Chargeback) => this.getSecurityProcedureString(row.securityProcedure) },
    { header: 'Card possession', cell: (row: Chargeback) => this.getCardStateString(row.cardState) },


    { header: 'Card details shared',
      cellColor: row => row.cardDetailsShared ? 'red':'green',
      cell: (row: Chargeback) => (row.cardDetailsShared || false) ? 'Yes':'No'
    },
    { header: 'Previous Purchase at Merchant',
      cellColor: row => row.previousPurchaseAtMerchant ? 'red':'green',
      cell: (row: Chargeback) => (row.previousPurchaseAtMerchant || false) ? 'Yes':'No'

    },
    {
      header: 'No running contract',
      cellColor: row => row.confirmNoRunningContract ? 'green':'red',
      cell: (row: Chargeback) => (row.confirmNoRunningContract || false) ? 'Yes':'No'

    },
    {
      header: 'Visited country in the last 12 months',
      cellColor: row => row.visitedCountryLast12m ? 'orange':'green',
      cell: (row: Chargeback) => (row.visitedCountryLast12m || false) ? 'Yes':'No'
    },

    { header: 'Other persons device access',
      cellColor: row => row.otherPersonsDeviceAccess ? 'red':'green',
      cell: (row: Chargeback) => (row.otherPersonsDeviceAccess || false) ? 'Yes':'No'
    },

    { header: 'Information fees and timeframe',
      cellColor: row => row.confirmInformationAboutFees ? 'green':'red',
      cell: (row: Chargeback) => (row.confirmInformationAboutFees || false) ? 'Yes':'No'
    },

    { header: 'Truthful',
      cellColor: row => row.confirmTruthful ? 'green':'red',
      cell: (row: Chargeback) => (row.confirmTruthful || false) ? 'Yes':'No'
    },


  ] as ContentRowField<Chargeback>[];

  fieldsTransaction = [
    { header: 'Merchant Name', cell: (row: Chargeback) => `${row.merchantName || ''}` },
    { header: 'Txn Date', cell: (row: Chargeback) => `${toDateTimeStringNoSeconds(row.transactionDate) || ''}` },
    { header: 'Txn Amount', cell: (row: Chargeback) => `${row.transactionAmount || '(unknown)' }` },


  ] as ContentRowField<Chargeback>[];

  fieldsTransactionDetail = [
    { header: 'Merchant Name', cell: (row: StatementLineDto) => `${row.merchName || ''}` },

    { header: 'Auth Date', cell: (row: StatementLineDto) => (row.authDate ? `${toDateTimeStringNoSeconds(row.authDate) || ''}` : '-') },
    { header: 'Settle Date', cell: (row: StatementLineDto) => (row.authDate ? `${toDateTimeStringNoSeconds(row.settleDate) || ''}` : '-') },
    { header: 'Reversal Date', cell: (row: StatementLineDto) => (row.authDate ? `${toDateTimeStringNoSeconds(row.reversalDate) || ''}` : '-') },

    { header: 'Txn Amt', cell: (row: StatementLineDto) => `${row.txnAmt || ''}` },
    { header: 'Txn Ccy', cell: (row: StatementLineDto) => `${row.txnCcy || ''}` },
    { header: 'Bill Amt', cell: (row: StatementLineDto) => `${row.billAmt || ''}` },
    { header: 'Bill Ccy', cell: (row: StatementLineDto) => `${row.billCcy || ''}` },
    { header: 'MCC', cell: (row: StatementLineDto) => `${row.mccCode || ''}` },
    { header: 'Desc', cell: (row: StatementLineDto) => `${row.description || ''}` },
    { header: 'GPS POS Data', cell: (row: StatementLineDto) => `${row.gpsPosData || ''}` },
    { header: 'Proc Code', cell: (row: StatementLineDto) => `${row.procCode || ''}` },



  ] as ContentRowField<StatementLineDto>[];


  fieldsSecondaries = [
    { header: 'GPS Status',
      cell: row => this.gpsCardStatusHelper.getIcon(row.gpsCardStatus as GpsCardStatus) + " " + this.gpsCardStatusHelper.getText(row.gpsCardStatus as GpsCardStatus),
    },
    { header: 'Secondary Token',
      cell: row => `${row.token}`,
      copyCellActive: row => true,
      link: row => row.id ? '../../cards/' + row.id : null,
    },
    { header: 'Product', cell: (row: CardStatusInfo) => `${row.productName || '(unknown)'}`},


  ] as ContentRowField<CardStatusInfo>[];


  fieldsOrder = [
    { header: 'Customer', icon: (row: Card) => 'visibility', link: (row: Card) => '../../customers-overview/' + (row.customer.id || ''), cell: (row: Card) => `${row.customer.firstName + ' ' + row.customer.lastName || ''}` } as ContentRowField<Card>,
    { header: 'Ident Level', iconColor: (row: Card) => {
        switch (row.customer.identLevel) {
          case IdentLevel.Fdd:
            return '#8bc34a';
          case IdentLevel.Edd:
            return '#207033';
          default:
            return null;
        }
      }, icon: (row: Card) => {
        switch (row.customer.identLevel) {
          case IdentLevel.Fdd:
            return 'beenhere';
          case IdentLevel.Edd:
            return 'thumb_up_alt';
          default:
            return null;
        }
      }, cell: (row: Card) => {
        switch (row.customer.identLevel) {
          case IdentLevel.Sdd:
            return 'SDD';
          case IdentLevel.Fdd:
            return 'FDD';
          case IdentLevel.Edd:
            return 'EDD';
          default:
            return 'unknown';
        }

      }
    },

    {
      header: 'Address Status',
      iconColor: (row: Card) => {

        if (row.customer.addressStatus == AddressStatus.Confirmed) {
          return '#8bc34a';
        }
        if (row.customer.addressStatus == AddressStatus.WaitingForDocuments) {
          return '#4a82c3';
        }
        if (row.customer.addressStatus == AddressStatus.PendingCheck) {
          return '#684ac3';
        }

        return '#d04848';
      },
      icon: (row: Card) => {
        if (row.customer.addressStatus == AddressStatus.Confirmed) {
          return 'place';
        }
        if (row.customer.addressStatus == AddressStatus.WaitingForDocuments) {
          return 'not_listed_location';
        }
        if (row.customer.addressStatus == AddressStatus.PendingCheck) {
          return 'not_listed_location';
        }

        return 'not_listed_location';
      }, cell: (row: Card) => {
        if (row.customer.addressStatus == AddressStatus.Confirmed) {
          return 'Confirmed';
        }
        if (row.customer.addressStatus == AddressStatus.WaitingForDocuments) {
          return 'Waiting for documents';
        }
        if (row.customer.addressStatus == AddressStatus.PendingCheck) {
          return 'Pending check';
        }

        return 'Unconfirmed';
      }},


    {
      header: 'Address Change Status',
      iconColor: (row: Customer) => {

        if (row.addressChangeStatus == AddressChangeStatus.WaitingForDocuments) {
          return '#4a82c3';
        }
        if (row.addressChangeStatus == AddressChangeStatus.PendingCheck) {
          return '#684ac3';
        }

        return '#ffffff';
      },
      icon: (row: Customer) => {
        if (row.addressChangeStatus == AddressChangeStatus.WaitingForDocuments) {
          return 'not_listed_location';
        }
        if (row.addressChangeStatus == AddressChangeStatus.PendingCheck) {
          return 'not_listed_location';
        }

        return '';
      }, cell: (row: Customer) => {
        if (row.addressChangeStatus == AddressChangeStatus.WaitingForDocuments) {
          return 'Waiting for documents';
        }
        if (row.addressChangeStatus == AddressChangeStatus.PendingCheck) {
          return 'Pending check';
        }

        return '';
      }},




    { header: 'Order', icon: (row: Card) => 'visibility', link: (row: Card) => '../../orders/' + (row.orderId || ''), cell: (row: Card) => `${row.orderId || ''}` },
    { header: 'Invoice', icon: (row: Card) => 'visibility', link: (row: Card) => '../../invoices/' + (row.order.invoiceId || ''), cell: (row: Card) => `${row.order.invoiceNumber || ''}` },

    { header: 'Order date', cell: (row: Card) => `${toDateString(row.orderDate) || ''}` },

    { header: 'Last Payment Reminder', cell: (row: Card) => `${(row.order.paymentEmailReminders && row.order.paymentEmailReminders[row.order.paymentEmailReminders.length - 1]) || ''}` },
  ] as ContentRowField<Card>[];

  public toDateTimeString = toDateTimeString;
  public logs: Array<EventLog>;
  currentLimitGroup: string|null;
  newLimitGroupToSelect: string|null;

  public gpsCardStatus = GpsCardStatus;
  currentGpsStatus: GpsCardStatus|null;
  currentGpsStatusCached: GpsCardStatus|null;
  newGpsStatusToSet: GpsCardStatus|null;
  _isQueryingCardStatus = false;
  currentGpsStatusDate: Date|null;
  chargebackId: number|null;
  gpsCardStatusHelper = GpsCardStatusHelper;

  newProductId: number|null;
  products: Product[] = [];
  otherCards: Card[] = [];

  chargebackComments: ObjectComment[] = [];
  commentsChargebackTarget = 7;


  imageUrl: string|null;
  limitGroups: LimitGroup[] = [];
  isLoadingOtherCards = false;
  card: Card|null;
  customer: Customer|null;
  txnDetail: StatementLineDto;
  gpsPosData: GPSPosData|null;
  isStatementLoading = false;
  isCardInfoLoading = false;
  isCardholderDetailsLoading = false;
  customerCards = [] as
    {
      id: number;
      card: Card;
      editVisible: boolean;
      editForm: FormGroup;
      invoiceComments: ObjectComment[];
      statement: Statement;
      financialStatements: any;
      cardholderDetails: CardHolderDetails;
      cardInfo: CardInfo;
    }[];

  fieldsCardholderDetails = [
    {header: "Sex", cell: (row: CardHolderDetails) => `${row.sex}`},
    {header: "Firstname", cell: (row: CardHolderDetails) => `${row.firstName}`},
    {header: "Lastname", cell: (row: CardHolderDetails) => `${row.lastName}`},
    {header: "E-Mail", cell: (row: CardHolderDetails) => `${row.email}`},
    {header: "Date of birth", cell: (row: CardHolderDetails) => `${toDateString(row.dateOfBirth) || ""}`},
    {header: "Mobile", cell: (row: CardHolderDetails) => `${formatPhone(row.phone)}`},
    {
      header: "Mobile",
      cellFlag: (row: CardHolderDetails) => phoneToCountry(row.mobile),
      cell: (row: CardHolderDetails) => `${row.mobile}`
    },
    {header: "Address Line 1", cell: (row: CardHolderDetails) => `${row.addrl1}`},
    {header: "Address Line 2", cell: (row: CardHolderDetails) => `${row.addrl2}`},
    {header: "Zip City", cell: (row: CardHolderDetails) => `${row.postCode} ${row.city}`},
  ];

  fieldsCardInfo = [
    {header: "Status", cell: (row: CardInfo) => `${row.statusText}`},
    {header: "PAN", cell: (row: CardInfo) => `${row.maskedPAN}`},
    {header: "Embossed name", cell: (row: CardInfo) => `${row.embossedName}`},
    {header: "Creation", cell: (row: CardInfo) => `${toDateString(row.creationDate) || ""}`},
    {header: "Expiry", cell: (row: CardInfo) => `${toDateString(row.expiryDate) || ""}`},
    {header: "Avail. balance", cell: (row: CardInfo) => `${row.currency} ${row.availableBalance}`},
    {header: "Blocked amount", cell: (row: CardInfo) => `${row.currency} ${row.blockedAmount}`},
    // { header: "Currency", cell: (row: CardInfo) => `${row.currency}` },
    {header: "Product", cell: (row: CardInfo) => `${row.product}`},
    {header: "Limit group", cell: (row: CardInfo) => `${row.limitGroupDesc}`},


  ];
  _isSettingChargebackStatus = false;


  constructor(
    private router: Router,
    private sanitizer: DomSanitizer,
    private location: Location,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private chargebackService: ChargebacksService,
    private cardsService: CardsService,
    private roleService: RoleService,
    private customersService: CustomersService,

    private logService: EventLogService,
    private _toastService: ToastService,
    private productService: ProductService,
    private objectCommentsService: ObjectCommentsService,
    private cardbroGatewayService: CardbroGatewayService,
    private snackBar: MatSnackBar,
  ) { }


  currentUserIsPartner() {
    return this.roleService.hasRoles('partner');
  }

  getCardholderDetails(card) {

    // test: "125130184"
    this.isCardholderDetailsLoading = true;

    this.cardbroGatewayService.getCardholderDetails(card.card.token).subscribe(data => {
        card.cardholderDetails = data;
        this.isCardholderDetailsLoading = false;
      },
      err => {
        this.showMessage(err.error.message || err.error || err.message);
        this.isCardholderDetailsLoading = false;
      });


  }

  getCardInfos(card) {


    this.isCardInfoLoading = true;

    this.cardbroGatewayService.getCardInfo(card.card.token).subscribe(data => {
        card.cardInfo = data;
        this.isCardInfoLoading = false;
      },
      err => {
        this.showMessage(err.error.message || err.error || err.message);
        this.isCardInfoLoading = false;
      });


  }

  getStatement(card) {

    this.isStatementLoading = true;
    this.cardbroGatewayService.getStatementEhi(card.card.token).subscribe(data => {
        card.statement = data;


        if (card.statement != null && card.statement.financialTransactions != null && card.statement.financialTransactions.length > 0) {

          card.statement.financialTransactions.forEach((item: Transaction) => {
            if (item.isCredit) {

            }

            if (item.isDebit) {
              item.amount = item.amount * -1;
              //item.billAmount = item.billAmount * -1;
            }

            item.description = item.description.replace(/\\/g, "\n");
            if (item.note === item.description) {
              item.note = null;
            }

            if (item.statusDescription == "AuthorizationReversed") {
              item.statusDescription = "Auth. Reversed";
            }


          });
        }


        card.financialStatements = new MatTableDataSource<Transaction>(card.statement.financialTransactions);
        card.financialStatements.paginator = this.paginatorStatement;
        this.isStatementLoading = false;
        // console.log( data.financialTransactions );
      },
      err => {
        this.showMessage(err.error.message || err.error || err.message);
        this.isStatementLoading = false;
      }
    );
  }


  showMessage(message) {
    this.snackBar.open(message, null, {
      duration: 5000,
      panelClass: ['app-mat-snack-bar']
    });
  }

  @ViewChild(MatPaginator, {static: false}) paginatorStatement: MatPaginator;

  ngOnInit() {

    this.route.paramMap.subscribe(
      (routeParam) => {
        const id = parseInt(this.route.snapshot.paramMap.get('id'));
        if ( id != this.chargebackId) {
          this.currentGpsStatus = null;
          this.chargebackId = id;
        }

        this.loadData();
        this.loadFilesList();
      },
      (err) => { }
    );

    //const id = parseInt(this.route.snapshot.paramMap.get("id"));
    //this.cardId = id;

    //this.loadData();

  }

  loadComments() {
    this.objectCommentsService.getForObject(this.chargebackId, this.commentsChargebackTarget).subscribe((data) => {
      this.chargebackComments = data;
    });
  }

  loadData() {
    this.isLoading = true;

    this.productService.getLimitGroups().subscribe(data => this.limitGroups = data);

    this.chargebackService.get(this.chargebackId).subscribe(result => {
      this.chargeback = result;
      this.card = this.chargeback.card;
      this.customer =this.chargeback.customer;
      if ( this.card.product.logoFileName)
        this.imageUrl = environment.apiUrl + this.card.product.logoFileName;

      this.currentGpsStatusDate = this.card.gpsCardStatusDate;


      this.logService.get( 16 /* Card */, this.card.id ).subscribe( data => {
        this.logs = data;
      });

      this.cardsService.getGpsCardStatusCached(this.card.token).subscribe(gpsCardStatusCached => {
        this.currentGpsStatusCached = gpsCardStatusCached;
      });

      this.isLoading = false;

      this.productService.getAll().subscribe(products => {
        this.products = products.data;
        this.newProductId = this.chargeback.card.productId;
      });

      this.cardsService.getForCustomer(this.card.customerId).subscribe(cards => {


        this.customerCards = [];
        cards.forEach(card => {
          this.customerCards.push({
            card,
            id: card.id,
            cardholderDetails: null,
            cardInfo: null,
            financialStatements: [],
            editForm: null,
            editVisible: false,
            invoiceComments: [],
            statement: null,
          });
        });



        this.isLoadingOtherCards = true;
        const otherCards = [];
        cards.forEach(value => {

          let isMySecondary = false;
          if (value.primaryCardId === this.card.id) {
            isMySecondary = true;
          }

          if ( !isMySecondary && value.id != this.chargeback.card.id && value.id != this.chargeback.card.primaryCardId ) {
            otherCards.push(value);
          }
        });
        this.otherCards = otherCards;
        this.isLoadingOtherCards = false;
      });

    });

    this.loadComments();
    this.loadTransactionDetail();

  }

  loadTransactionDetail() {
    let txn = this.chargebackService.getTransactionDetail(this.chargebackId).subscribe(
      data => {

        this.gpsPosData = parseGPSPosData(data.gpsPosData);
        this.txnDetail = data;
      }
    );

  }

  loadFilesList() {
    let txn = this.chargebackService.getFilesList(this.chargebackId).subscribe(
      data => {

        this.filesList = data;
      }
    );

  }


  returnBack() {
    this.location.back();
  }

  showLogs() {
    return this.roleService.hasRoles(['admin', 'manager', 'agent', 'compliance']);
  }


  cardStatusChangedCallback(ok, gpsCardStatusToSet: GpsCardStatus, includeSecondaries: boolean|null) {

    this._isQueryingCardStatus = false;
    this._toastService.success('Card ' + this.card.token + ' status was set to ' + gpsCardStatusToSet);

    this.currentGpsStatus = gpsCardStatusToSet;
    this.currentGpsStatusCached = gpsCardStatusToSet;
    this.currentGpsStatusDate = new Date();

    /*if ( includeSecondaries === true) {
      this.card.secondaryCards.forEach(value => value.gpsCardStatus = gpsCardStatusToSet);
    }*/


    this.loadComments();
  }


  setChargebackStatus() {
    if ( this.newChargebackStatusToSet == null )
      return;

    this._isSettingChargebackStatus = true;

    this.dialog.open( StatusChangeReasonDialogComponent, {data:{
        title: 'Confirm Chargeback Status',
        text: 'This status needs a reason to show display to the card holder.',
        subText: 'Please enter a reason to be shown to the card holder.',
        confirm: 'Ok',
        cancel: 'Cancel',
        selectableReasons: [],
        customReasonAllowed: true,
      }}).afterClosed().subscribe( result => {
      if ( result ) {

        this.chargebackService.setStatus(this.chargebackId, this.newChargebackStatusToSet, result).subscribe(data => {
          this.loadData();
          this._isSettingChargebackStatus = false;
        })
      } else {
        this.newGpsStatusToSet = this.currentGpsStatus || this.currentGpsStatusCached;
        this._toastService.info('Chargeback status will not be changed');
        this._isSettingChargebackStatus = false;
      }
    } );




  }

  setGpsCardStatus(gpsCardStatusToSet: GpsCardStatus, confirmIrreversible: boolean = false, reason: string|null = null, reasonStatement: string|null = null, includeSecondaries: boolean|null = null) {

    this._isQueryingCardStatus = true;

    this.cardsService.setGpsCardStatus(this.card.token, gpsCardStatusToSet, confirmIrreversible, reason, reasonStatement, includeSecondaries).subscribe(ok => this.cardStatusChangedCallback(ok, gpsCardStatusToSet, includeSecondaries), err => {

      if (err instanceof HttpErrorResponse && err.status == 403 && err.error.message === 'insufficient_permissions') {

        this._toastService.error('Insufficient permissions, need ' + err.error.requiredRoles);
        this._isQueryingCardStatus = false;
        return;
      } else if (err instanceof HttpErrorResponse && err.status == 422 && err.error.message === 'reason_needed') {
        this._toastService.warn('this status needs a internal reason');

        this.dialog.open( StatusChangeReasonDialogComponent, {data:{
            title: 'Confirm Card Status ' + gpsCardStatusToSet,
            text: 'Card Status ' + gpsCardStatusToSet + ' needs a reason (internal display only).',
            subText: 'Please enter a internal reason for the status change.',
            confirm: 'Ok',
            cancel: 'Cancel',
            selectableReasons: err.error.possibleReasons,
            customReasonAllowed: err.error.customReasonAllowed,
          }}).afterClosed().subscribe( result => {
          if ( result ) {
            this.setGpsCardStatus(gpsCardStatusToSet, confirmIrreversible, result, reasonStatement, includeSecondaries);
          } else {
            this.newGpsStatusToSet = this.currentGpsStatus || this.currentGpsStatusCached;
            this._toastService.info('Card status will not be changed');
            this._isQueryingCardStatus = false;
          }
        } );

      } else if (err instanceof HttpErrorResponse && err.status == 422 && err.error.message === 'reason_statement_needed') {
        this._toastService.warn('this status needs a reason to display on the card statement');

        this.dialog.open( StatusChangeReasonDialogComponent, {data:{
            title: 'Confirm Card Status ' + gpsCardStatusToSet,
            text: 'Card Status ' + gpsCardStatusToSet + ' needs a reason to show on the card statement.',
            subText: 'Please enter a reason to be shown on the card statement for the status change.',
            confirm: 'Ok',
            cancel: 'Cancel',
            selectableReasons: err.error.possibleReasons,
            customReasonAllowed: err.error.customReasonAllowed,
          }}).afterClosed().subscribe( result => {
          if ( result ) {
            this.setGpsCardStatus(gpsCardStatusToSet, confirmIrreversible, reason, result, includeSecondaries);
          } else {
            this.newGpsStatusToSet = this.currentGpsStatus || this.currentGpsStatusCached;
            this._toastService.info('Card status will not be changed');
            this._isQueryingCardStatus = false;
          }
        } );

      }


      else if (err instanceof HttpErrorResponse && err.status == 422 && err.error.message === 'confirmation_needed') {
        this._toastService.warn('this status needs confirmation');

        this.dialog.open( ConfirmDialogComponent, {data:{
            title: 'Confirm Card Status ' + gpsCardStatusToSet,
            text: 'Card Status ' + gpsCardStatusToSet + ' is irreversible. Are you sure you want to set this status?',
            confirm: 'Ok',
            cancel: 'Cancel'
          }}).afterClosed().subscribe( result => {
          if ( result == 'ok' ) {
            this.setGpsCardStatus(gpsCardStatusToSet, true, reason, reasonStatement, includeSecondaries);
          } else {
            this.newGpsStatusToSet = this.currentGpsStatus || this.currentGpsStatusCached;
            this._toastService.info('Card status will not be changed');
          }
        } );

      }
      else if (err instanceof HttpErrorResponse && err.status == 422 && err.error.message === 'include_secondaries_question') {
        this._toastService.warn('Propagate to secondary cards?');

        this.dialog.open( IncludeSecondariesDialogComponent, {data:{
            title: 'Set Card Status on secondary cards?',
            text: 'Do you also want to set status ' + gpsCardStatusToSet + ' on secondary cards?',
            secondaryTokens: err.error.secondaryTokens,
          }}).afterClosed().subscribe( result => {
          if ( result == 'yes' ) {
            this.setGpsCardStatus(gpsCardStatusToSet, true, reason, reasonStatement, true);
          } else {
            this.setGpsCardStatus(gpsCardStatusToSet, true, reason, reasonStatement, false);
          }
        } );

      }
      else {
        this._toastService.error(err.error.message || err.message || err.error || err);
      }

      this._isQueryingCardStatus = false;


    });
  }


  queryCardStatusFromPortal() {
    this._isQueryingCardStatus = true;
    this.cardsService.getGpsCardStatus(this.card.token).subscribe(gpsCardStatus => {
      this._isQueryingCardStatus = false;
      this.currentGpsStatus = gpsCardStatus;
    }, err => {
      this._isQueryingCardStatus = false;
      this._toastService.error(err.error.message || err.message || err.error || err);
    });
  }


  currentUserIsAdmin(): boolean {
    return this.roleService.hasRoles('admin');
  }
  currentUserIsSuperAdmin(): boolean {
    return this.roleService.hasRoles( 'super_admin' );
  }

  currentUserIsCompliance(): boolean {
    return this.roleService.hasRoles('compliance');
  }
  currentUserIsComplianceManager(): boolean {
    return this.roleService.hasRoles('compliance_manager');
  }
  currentUserIsFinanceManager(): boolean {
    return this.roleService.hasRoles('finance_manager');
  }


  protected readonly SCATestResult = SCATestResult;
  protected readonly CardholderPresent = CardholderPresent;
  protected readonly CardPresent = CardPresent;
  protected readonly CardDataInputMethod = CardDataInputMethod;
  displayedStatementColumns: string[] = ["direction", "transactionDate", "description", "amount", "blockAmount", "billAmount", "currency", "rateFee", "fixedFee", "statusDescription"];
  newChargebackStatusToSet: ChargebackStatus|null;
  chargebackStatus = ChargebackStatus;



  isPinAuth(gpsPosData: GPSPosData|null) : boolean {
    if ( gpsPosData == null ) return false;
    return (gpsPosData.cardholderAuthMethod1) == AuthenticationMethod.PIN || (gpsPosData.cardholderAuthMethod2) == AuthenticationMethod.PIN || (gpsPosData.cardholderAuthMethod3) == AuthenticationMethod.PIN || (gpsPosData.cardholderAuthMethod4 ?? false) == AuthenticationMethod.PIN;
  }

  isSecure3DsAuth(gpsPosData: GPSPosData|null) : boolean {
    if ( gpsPosData == null ) return false;
    return (gpsPosData.cardholderAuthMethod1) == AuthenticationMethod.Secure3DS || (gpsPosData.cardholderAuthMethod2) == AuthenticationMethod.Secure3DS || (gpsPosData.cardholderAuthMethod3) == AuthenticationMethod.Secure3DS || (gpsPosData.cardholderAuthMethod4 ?? false) == AuthenticationMethod.Secure3DS;
  }

  isBiometricsAuth(gpsPosData: GPSPosData|null) : boolean {
    if ( gpsPosData == null ) return false;
    return (gpsPosData.cardholderAuthMethod1) == AuthenticationMethod.Biometrics || (gpsPosData.cardholderAuthMethod2) == AuthenticationMethod.Biometrics || (gpsPosData.cardholderAuthMethod3) == AuthenticationMethod.Biometrics || (gpsPosData.cardholderAuthMethod4 ?? false) == AuthenticationMethod.Biometrics;
  }

  isGpsButtonEnabled() {
    //const ok = this.roleService.hasRoles(["admin", "manager", "compliance"]);

    if (this.roleService.hasRoles(["partner", "partner_support"])) {
      return false;
    }
    return true;

  }

  urlSafe: SafeResourceUrl;
  isImgHidden = true;
  isFrameHidden = true;
  filename = '';
  errorMsg = '';

  openFile(fileId: number) {
    this.customersService.getFileInline(fileId).subscribe(
      result => {

        const disposition = result.headers.get('Content-Disposition');
        const contentType = result.headers.get('Content-Type');
        const filename = disposition.split(';')[1].trim().split('=')[1];

        const blob = new Blob([result.body], {type: contentType});
        //FileSaver.saveAs(blob , filename);
        const fileURL = URL.createObjectURL(blob);


        this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(fileURL);

        if (contentType.indexOf('image') >= 0) {
          this.isImgHidden = false;
          this.isFrameHidden = true;

        } else {
          this.isImgHidden = true;
          this.isFrameHidden = false;

        }
        this.errorMsg = null;
        this.filename = filename;


        //FileHelper.saveFile( result );
      },
      err => {
        this.filename = null;
        this.isImgHidden = true;
        this.isFrameHidden = true;
        this.errorMsg = 'Error occured during downloading the file.';

        this.snackBar.open(err.error.message || err.message || err.error || err, null, {
          duration: 5000,
          panelClass: ['app-mat-snack-bar']
        });
      }
    );
  }


  getChargebackUploadTypeText(ctf: ChargebackToFile) {
    if ( ctf.fileType == ChargebackFileType.MerchantCommunication )
      return "Merchant communication";
    if ( ctf.fileType == ChargebackFileType.PurchaseConfirmation)
      return "Purchase confirmation";
    if ( ctf.fileType == ChargebackFileType.PoliceReport)
      return "Police report";
  }

  private getStatusString(status: ChargebackStatus) {
    if ( status == ChargebackStatus.Request ) return "Requested";
    if ( status == ChargebackStatus.Pending ) return "Pending";
    if ( status == ChargebackStatus.Denied ) return "Denied";
    if ( status == ChargebackStatus.SuccessPending ) return "Approved";
    if ( status == ChargebackStatus.ChargebackExecuted ) return "Charged back";
    return "-";
  }
}
